import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import logoImage from "@assets/logo-e-zez-png-3.png";
import { galleryImages as staticGalleryImages } from '@/data/galleryImages';

// Type definition for gallery images
interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  category: string;
  tags: string[];
  uploadedBy?: string;
  uploadedAt?: string;
  projectDate?: string;
  projectId?: string;
  isPrimary?: boolean;
}

// ─── Video Projects Management Tab ───────────────────────────────────────────

interface VideoItem {
  id: number;
  videoUrl: string;
  platform: string;
  embedUrl: string;
  title?: string;
  description?: string;
  category: string;
  tags: string[];
  createdAt: string;
}

const VIDEO_CATEGORIES = [
  { value: 'cable-railing', label: 'Cable Railing' },
  { value: 'indoor-railing', label: 'Indoor Railing' },
  { value: 'ornamental-work', label: 'Ornamental Work' },
  { value: 'outdoor-railing', label: 'Outdoor Railing' },
  { value: 'spiral-stairs', label: 'Spiral Stairs' },
  { value: 'stainless-steel', label: 'Stainless Steel' },
  { value: 'steel-fabrication', label: 'Steel Fabrication' },
];

function VideoProjectsTab() {
  const { toast } = useToast();
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [addForm, setAddForm] = useState({ videoUrl: '', title: '', description: '', category: '', tags: '' });

  interface VideoEditForm {
    id?: number; videoUrl?: string; platform?: string; embedUrl?: string;
    title?: string; description?: string; category?: string; createdAt?: string; tags: string;
  }
  const [editForm, setEditForm] = useState<VideoEditForm>({ tags: '' });

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/videos');
      const data = await res.json();
      if (data.success) setVideos(data.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.videoUrl || !addForm.category) {
      toast({ variant: 'destructive', title: 'Missing fields', description: 'Video URL and Category are required.' });
      return;
    }
    try {
      const res = await fetch('/api/videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast({ title: 'Video added', description: 'The video has been added to the gallery.' });
      setAddForm({ videoUrl: '', title: '', description: '', category: '', tags: '' });
      fetchVideos();
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Failed to add video', description: err.message });
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !editForm.category) return;
    try {
      const res = await fetch(`/api/videos/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast({ title: 'Video updated', description: 'Changes saved successfully.' });
      setEditingId(null);
      fetchVideos();
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Update failed', description: err.message });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this video? This cannot be undone.')) return;
    try {
      const res = await fetch(`/api/videos/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to delete');
      toast({ title: 'Video deleted' });
      fetchVideos();
    } catch {
      toast({ variant: 'destructive', title: 'Delete failed' });
    }
  };

  const startEdit = (v: VideoItem) => {
    setEditingId(v.id);
    const { tags, ...rest } = v;
    setEditForm({ ...rest, tags: tags.join(', ') });
  };

  const PlatformBadge = ({ platform }: { platform: string }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
      platform === 'youtube' ? 'bg-red-100 text-red-700' :
      platform === 'vimeo' ? 'bg-blue-100 text-blue-700' :
      'bg-blue-600 text-white'
    }`}>{platform}</span>
  );

  return (
    <div className="space-y-8">
      {/* Add Video Form */}
      <Card className="p-6 shadow-md border-t-4 border-red-600">
        <h3 className="text-xl font-bold mb-1">Add Video Project</h3>
        <p className="text-sm text-gray-500 mb-5">
          Paste any Facebook, YouTube, or Vimeo video link — we handle the rest.
        </p>
        <form onSubmit={handleAdd} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Video URL <span className="text-red-500">*</span></label>
            <Input
              type="url"
              placeholder="e.g. https://www.facebook.com/SwissWeldingLLC/videos/12345..."
              value={addForm.videoUrl}
              onChange={e => setAddForm(p => ({ ...p, videoUrl: e.target.value }))}
              required
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Supported: Facebook, YouTube, Vimeo</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input placeholder="e.g. Custom Cable Railing Installation"
                value={addForm.title}
                onChange={e => setAddForm(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category <span className="text-red-500">*</span></label>
              <select className="w-full p-2 border rounded text-sm"
                value={addForm.category}
                onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))} required>
                <option value="">Select Category</option>
                {VIDEO_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea placeholder="Brief description of this video project..."
              value={addForm.description}
              onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tags</label>
            <Input placeholder="metal, outdoor, stainless-steel (comma separated)"
              value={addForm.tags}
              onChange={e => setAddForm(p => ({ ...p, tags: e.target.value }))} />
          </div>
          <Button type="submit" className="w-full bg-black hover:bg-red-600 text-white font-semibold transition-colors">
            Add Video to Gallery
          </Button>
        </form>
      </Card>

      {/* Existing Videos */}
      <Card className="p-6 shadow-md">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xl font-semibold">Manage Videos</h3>
          <Button variant="outline" size="sm" onClick={fetchVideos} className="text-xs">Refresh</Button>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-center text-gray-400 py-10">No videos yet. Add your first video above.</p>
        ) : (
          <div className="space-y-4">
            {videos.map(v => (
              <div key={v.id} className="border rounded-xl overflow-hidden shadow-sm">
                {/* Video card header */}
                <div className="flex items-start gap-4 p-4 bg-gray-50">
                  {/* Embed thumbnail */}
                  <div className="flex-shrink-0 w-40 aspect-video rounded-lg overflow-hidden bg-gray-200 relative">
                    <iframe
                      src={v.embedUrl}
                      title={v.title || 'Video'}
                      className="w-full h-full scale-105 pointer-events-none"
                      style={{ border: 'none' }}
                      tabIndex={-1}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <PlatformBadge platform={v.platform} />
                      <span className="text-xs text-gray-400 capitalize">{v.category.replace(/-/g, ' ')}</span>
                    </div>
                    <h4 className="font-semibold truncate">{v.title || <span className="text-gray-400 italic">No title</span>}</h4>
                    {v.description && <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{v.description}</p>}
                    <p className="text-xs text-gray-400 mt-1 truncate font-mono">{v.videoUrl}</p>
                    {v.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {v.tags.map((t, i) => <span key={i} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => startEdit(v)} className="text-xs">Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(v.id)} className="text-xs">Delete</Button>
                  </div>
                </div>

                {/* Inline edit form */}
                {editingId === v.id && (
                  <form onSubmit={handleEdit} className="p-4 bg-blue-50 border-t space-y-4">
                    <h4 className="font-semibold text-sm text-blue-800">Edit Video</h4>
                    <div>
                      <label className="block text-xs font-medium mb-1">Video URL</label>
                      <Input type="url" className="font-mono text-sm"
                        value={editForm.videoUrl || ''}
                        onChange={e => setEditForm(p => ({ ...p, videoUrl: e.target.value }))} />
                      <p className="text-xs text-gray-400 mt-0.5">Leave unchanged or paste a new URL</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium mb-1">Title</label>
                        <Input value={editForm.title || ''}
                          onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Category <span className="text-red-500">*</span></label>
                        <select className="w-full p-2 border rounded text-sm"
                          value={editForm.category || ''}
                          onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} required>
                          <option value="">Select Category</option>
                          {VIDEO_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Description</label>
                      <Textarea value={editForm.description || ''}
                        onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Tags (comma separated)</label>
                      <Input value={editForm.tags || ''}
                        onChange={e => setEditForm(p => ({ ...p, tags: e.target.value }))} />
                    </div>
                    <div className="flex gap-3">
                      <Button type="submit" className="bg-black hover:bg-primary text-white text-sm">Save Changes</Button>
                      <Button type="button" variant="outline" className="text-sm"
                        onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Main Admin Component ─────────────────────────────────────────────────────

export default function Admin() {
  const [, navigate] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    images: [] as File[],
    alt: '',
    title: '',
    description: '',
    category: '',
    tags: '',
    primaryImageIndex: 0,
    projectDate: '',
  });

  // Edit form state
  const [editForm, setEditForm] = useState<GalleryImage | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);

  // State for gallery management
  const [loadingState, setLoadingState] = useState(true);
  const [galleryImagesData, setGalleryImagesData] = useState<GalleryImage[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch gallery images from API
  const fetchGalleryImages = async () => {
    try {
      setLoadingState(true);
      setError(null);

      const response = await fetch('/api/gallery');
      if (!response.ok) {
        throw new Error('Failed to fetch gallery images from API');
      }

      const data = await response.json();
      if (data.success) {
        setGalleryImagesData(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load gallery images');
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setError('Failed to load gallery images. Please check your connection.');
      setGalleryImagesData([]);
    } finally {
      setLoadingState(false);
    }
  };

  // Load gallery images on component mount
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  // Expose these variables for use in the component
  const isLoading = loadingState;
  const galleryImages = galleryImagesData;

  // Real refetch function
  const refetch = () => {
    fetchGalleryImages();
  };

  // Function to handle selecting an image for editing
  const handleSelectImage = (image: GalleryImage) => {
    setSelectedImage(image);
    setEditForm(image);
    setIsEditing(true);
  };

  // Function to handle updating image details
  const handleUpdateImage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editForm || !selectedImage) {
      toast({
        variant: "destructive",
        title: "Missing data",
        description: "No image selected for editing.",
      });
      return;
    }

    try {
      // If we have a new image file, upload it first
      if (newImage) {
        const formData = new FormData();
        formData.append('image', newImage);

        const imageResponse = await fetch(`/api/gallery/${selectedImage.id}/image`, {
          method: 'PUT',
          body: formData,
          credentials: 'include'
        });

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json();
          throw new Error(errorData.message || 'Failed to update image file');
        }
      }

      // Update the image metadata
      const metadataResponse = await fetch(`/api/gallery/${selectedImage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          alt: editForm?.alt || '',
          title: editForm?.title || '',
          description: editForm?.description || '',
          category: editForm?.category || '',
          tags: Array.isArray(editForm?.tags) ? editForm.tags : [],
        }),
        credentials: 'include'
      });

      if (!metadataResponse.ok) {
        const errorData = await metadataResponse.json();
        throw new Error(errorData.message || 'Failed to update image details');
      }

      toast({
        title: "Image updated",
        description: "The gallery image has been updated successfully.",
      });

      // Reset form state
      setIsEditing(false);
      setSelectedImage(null);
      setEditForm(null);
      setNewImage(null);

      // Refresh gallery images
      refetch();

    } catch (error) {
      console.error('Error updating image:', error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "There was an error updating the image. Please try again.",
      });
    }
  };

  // Function to handle deleting an image
  const handleDeleteImage = async (imageId: number) => {
    if (!confirm("Are you sure you want to delete this image? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch(`/api/gallery/${imageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete image');
      }

      // Reset form if we were editing this image
      if (editForm && editForm.id === imageId) {
        setIsEditing(false);
        setSelectedImage(null);
        setEditForm(null);
      }

      toast({
        title: "Image deleted",
        description: "The gallery image has been successfully deleted.",
      });

      // Refetch the gallery images
      refetch();

    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        variant: "destructive",
        title: "Deletion failed",
        description: error instanceof Error ? error.message : "There was an error deleting the image. Please try again.",
      });
    }
  };

  // Function to handle uploading new images
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadForm.images.length || !uploadForm.alt || !uploadForm.category) {
      toast({
        variant: "destructive",
        title: "Missing required fields",
        description: "At least one image file, alt text and category are required.",
      });
      return;
    }

    try {
      // Create form data for the upload
      const formData = new FormData();

      // Append all selected images
      uploadForm.images.forEach((image) => {
        formData.append('images', image);
      });

      formData.append('alt', uploadForm.alt);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('tags', uploadForm.tags);

      // Add primary image index
      formData.append('primaryImageIndex', uploadForm.primaryImageIndex.toString());

      // Make API call to upload the images
      const response = await fetch('/api/gallery', {
        method: 'POST',
        body: formData,
        credentials: 'include'
        // Don't set Content-Type header - let the browser set it with boundary
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to upload images';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      toast({
        title: "Project uploaded",
        description: `New gallery project with ${uploadForm.images.length} image(s) has been successfully added.`,
      });

      // Reset form after successful upload
      setUploadForm({
        images: [],
        alt: '',
        title: '',
        description: '',
        category: '',
        tags: '',
        primaryImageIndex: 0,
        projectDate: '',
      });

      // Clear the file input
      const fileInput = document.querySelector('input[type="file"][multiple]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Refetch the gallery images
      refetch();

    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading the images. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Admin Header */}
      <div className="bg-black text-white py-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Back Button Row */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <img src={logoImage} alt="Swiss Welding" className="h-12 w-auto" />
                <div className="flex flex-col">
                  <h1 className="text-xl md:text-2xl font-bold">Admin Dashboard</h1>
                  {user && (
                    <div className="text-sm text-primary">
                      Logged in as <span className="font-semibold">{user.username}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Button 
                variant="default" 
                onClick={() => logoutMutation.mutate()}
                className="bg-primary hover:bg-white hover:text-black"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Navigation Links Row */}
          <div className="hidden md:flex justify-center space-x-6 mt-2">
            <button 
              onClick={() => navigate('/')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/#about')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ABOUT
            </button>
            <button 
              onClick={() => navigate('/#services')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              SERVICES
            </button>
            <button 
              onClick={() => navigate('/gallery')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              GALLERY
            </button>
            <button 
              onClick={() => navigate('/#testimonials')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TESTIMONIALS
            </button>
            <button 
              onClick={() => navigate('/#timeline')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TIMELINE
            </button>
            <button 
              onClick={() => navigate('/#estimation')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ESTIMATE
            </button>
            <button 
              onClick={() => navigate('/#booking')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              BOOK NOW
            </button>
            <button 
              onClick={() => navigate('/#contact')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              CONTACT
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">Gallery Management</h2>
          <div>
            <Button
              variant="outline"
              onClick={() => navigate('/gallery')}
              className="border-black text-black hover:bg-black hover:text-white mr-2"
            >
              View Gallery
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/bookings')} 
              className="border-black text-black hover:bg-black hover:text-white"
            >
              Manage Bookings
            </Button>
          </div>
        </div>

        <Tabs defaultValue="manage" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="manage">Manage Photos</TabsTrigger>
            <TabsTrigger value="upload">Upload Photos</TabsTrigger>
            <TabsTrigger value="videos">Video Projects</TabsTrigger>
          </TabsList>

          {/* Manage Existing Images Tab */}
          <TabsContent value="manage" className="space-y-4">
            <Card className="p-6 shadow-md">
              <h3 className="text-xl font-semibold mb-4">Gallery Images</h3>

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block mb-2"></div>
                  <p>Loading gallery images...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600">
                  <p className="mb-4">{error}</p>
                  <Button
                    onClick={refetch}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Try Again
                  </Button>
                </div>
              ) : !galleryImages || galleryImages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No gallery images found. Upload some images to get started.</p>
                </div>
              ) : (
                <div>
                  {/* Grid of existing images grouped by project */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {(() => {
                      // Group images by projectId, treating null/undefined as individual images
                      const groupedImages = galleryImages.reduce((acc, image) => {
                        const key = image.projectId || `single-${image.id}`;
                        if (!acc[key]) {
                          acc[key] = [];
                        }
                        acc[key].push(image);
                        return acc;
                      }, {} as Record<string, typeof galleryImages>);

                      return Object.entries(groupedImages).map(([projectKey, images]) => {
                        const primaryImage = images.find(img => img.isPrimary) || images[0];
                        const imageCount = images.length;
                        const isProject = !!primaryImage.projectId && imageCount > 1;

                        return (
                          <div key={projectKey} className="space-y-4">
                            <div className="border rounded-lg overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <div className="relative aspect-[4/3] bg-gray-100" style={{ minHeight: '220px' }}>
                                {primaryImage.src ? (
                                  <img 
                                    src={primaryImage.src} 
                                    alt={primaryImage.alt} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      console.error('Failed to load image:', primaryImage.src);
                                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <span className="text-gray-500">No Image</span>
                                  </div>
                                )}
                                {isProject && (
                                  <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                                    {imageCount} images
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                                  <Button
                                    onClick={() => handleSelectImage(primaryImage)}
                                    className="bg-primary hover:bg-white hover:text-black text-white mr-2"
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      if (isProject) {
                                        if (confirm(`This will delete all ${imageCount} images in this project. Are you sure?`)) {
                                          images.forEach(img => handleDeleteImage(img.id));
                                        }
                                      } else {
                                        handleDeleteImage(primaryImage.id);
                                      }
                                    }}
                                    variant="destructive"
                                  >
                                    Delete {isProject ? 'Project' : 'Image'}
                                  </Button>
                                </div>
                              </div>
                              <div className="p-4">
                                <h4 className="font-semibold text-lg truncate">{primaryImage.title || 'Untitled'}</h4>
                                <p className="text-sm text-gray-500 truncate">{primaryImage.category}</p>
                                {primaryImage.projectDate && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    Date: {new Date(primaryImage.projectDate).toLocaleDateString()}
                                  </p>
                                )}
                                {isProject && (
                                  <p className="text-xs text-blue-600 mt-1">{imageCount} images in this project</p>
                                )}
                              </div>
                            </div>

                            {/* Inline Edit Form - Show if this specific image is being edited */}
                            {isEditing && selectedImage && selectedImage.id === primaryImage.id && (
                              <Card className="p-6 border-2 border-primary w-full">
                                <h3 className="text-xl font-semibold mb-4">Edit Image</h3>

                                <form onSubmit={handleUpdateImage} className="space-y-5 w-full">
                                  {/* Current Image Preview */}
                                  <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Current Image</label>
                                    <div className="aspect-video w-full max-w-md rounded-lg overflow-hidden border">
                                      <img 
                                        src={selectedImage.src} 
                                        alt={selectedImage.alt} 
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  </div>

                                  {/* Upload New Image */}
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Replace Image (Optional)</label>
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => setNewImage(e.target.files?.[0] || null)}
                                    />
                                    <p className="text-sm text-gray-500 mt-1">Leave empty to keep the current image</p>
                                  </div>

                                  {/* Edit Fields */}
                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Alt Text</label>
                                    <Input
                                      type="text"
                                      placeholder="Alternative text for the image (required)"
                                      value={editForm?.alt || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, alt: e.target.value } : null)}
                                      required
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Project Title</label>
                                    <Input
                                      type="text"
                                      placeholder="Enter a title for this project"
                                      value={editForm?.title || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, title: e.target.value } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Project Description</label>
                                    <Textarea
                                      placeholder="Enter a detailed description of this project"
                                      value={editForm?.description || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, description: e.target.value } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Category</label>
                                    <select
                                      className="w-full p-2 border rounded"
                                      value={editForm?.category || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, category: e.target.value } : null)}
                                      required
                                    >
                                      <option value="">Select Category</option>
                                      <option value="cable-railing">Cable Railing</option>
                                      <option value="indoor-railing">Indoor Railing</option>
                                      <option value="ornamental-work">Ornamental Work</option>
                                      <option value="outdoor-railing">Outdoor Railing</option>
                                      <option value="spiral-stairs">Spiral Stairs</option>
                                      <option value="stainless-steel">Stainless Steel</option>
                                      <option value="steel-fabrication">Steel Fabrication</option>
                                      {/* Legacy categories */}
                                      <option value="railings">Railings (Legacy)</option>
                                      <option value="structural">Structural (Legacy)</option>
                                      <option value="custom">Custom (Legacy)</option>
                                      <option value="commercial">Commercial (Legacy)</option>
                                    </select>
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Tags</label>
                                    <Textarea
                                      placeholder="Enter tags separated by commas (e.g., metal, outdoor, stairs)"
                                      value={Array.isArray(editForm?.tags) ? editForm.tags.join(', ') : ''}
                                      onChange={(e) => setEditForm(prev => prev ? { 
                                        ...prev, 
                                        tags: e.target.value.split(',').map((tag: string) => tag.trim()).filter(Boolean)
                                      } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="flex gap-3 w-full">
                                    <Button type="submit" className="flex-1 bg-black hover:bg-primary text-white">
                                      Update Image
                                    </Button>
                                    <Button 
                                      type="button" 
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => {
                                        setIsEditing(false);
                                        setSelectedImage(null);
                                        setEditForm(null);
                                        setNewImage(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </form>
                              </Card>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}


            </Card>
          </TabsContent>

          {/* Upload New Image Tab */}
          <TabsContent value="upload">
            <Card className="p-6 shadow-md">
              <h3 className="text-xl font-semibold mb-4">Upload New Image</h3>

              <form onSubmit={handleUpload} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-1">Upload Images</label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setUploadForm(prev => ({ ...prev, images: files }));
                    }}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    You can select multiple images for this project. The first image will be the primary one.
                  </p>
                  {uploadForm.images.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700">
                        Selected: {uploadForm.images.length} image(s)
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {uploadForm.images.map((file, index) => (
                          <span
                            key={index}
                            className={`text-xs px-2 py-1 rounded ${
                              index === uploadForm.primaryImageIndex
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {file.name} {index === uploadForm.primaryImageIndex && '(Primary)'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Alt Text</label>
                  <Input
                    type="text"
                    placeholder="Alternative text for the image (required)"
                    value={uploadForm.alt}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, alt: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Project Title</label>
                  <Input
                    type="text"
                    placeholder="Enter a title for this project"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Project Description</label>
                  <Textarea
                    placeholder="Enter a detailed description of this project"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">Select Category</option>
                    <option value="cable-railing">Cable Railing</option>
                    <option value="indoor-railing">Indoor Railing</option>
                    <option value="ornamental-work">Ornamental Work</option>
                    <option value="outdoor-railing">Outdoor Railing</option>
                    <option value="spiral-stairs">Spiral Stairs</option>
                    <option value="stainless-steel">Stainless Steel</option>
                    <option value="steel-fabrication">Steel Fabrication</option>
                    {/* Legacy categories */}
                    <option value="railings">Railings (Legacy)</option>
                    <option value="structural">Structural (Legacy)</option>
                    <option value="custom">Custom (Legacy)</option>
                    <option value="commercial">Commercial (Legacy)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tags</label>
                  <Textarea
                    placeholder="Enter tags separated by commas (e.g., metal, outdoor, stairs)"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  />
                </div>

                <Button type="submit" className="w-full bg-black hover:bg-primary text-white">
                  Upload Image
                </Button>
              </form>
            </Card>
          </TabsContent>
          {/* Video Projects Tab */}
          <TabsContent value="videos">
            <VideoProjectsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Home Button */}
      <div className="fixed bottom-8 right-8 z-10">
        <Button
          onClick={() => navigate('/')}
          className="bg-black text-white hover:bg-primary shadow-xl rounded-full p-4 h-14 w-14 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </Button>
      </div>

      {/* Footer */}
      <div className="bg-black text-white py-6 text-center mt-auto">
        <p>© {new Date().getFullYear()} Swiss Welding LLC. All rights reserved.</p>
      </div>
    </div>
  );
}